import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AppConfigStore } from '../../../../../state/app-config.repository';
import { ColorPicker } from '../../../../../shared/components/form-fields/color-picker/color-picker';
import { luminance, contrast } from '../../../../../shared/helpers/color';
import { TAppColorMode } from '../../../../../shared/schemas/settings';
import { ImageUpload } from '../../../../../shared/components/file-upload/image-upload';
import { ImageUploadItem } from '../../../../../shared/components/file-upload/models';
import { configDefault } from '../../../../../shared/constants/app-config-default';
import { fileToDataUrl } from '../../../../../shared/helpers/upload-image';

@Component({
  selector: 'andor-settings-theme',
  standalone: true,
  imports: [TranslatePipe, ColorPicker, ImageUpload],
  templateUrl: './settings-theme.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-theme.scss',
})
export class SettingsTheme {
  mode = input<TAppColorMode>('dark');

  protected readonly appConfigStore = inject(AppConfigStore);
  private readonly translateService = inject(TranslateService);

  private readonly assets = toSignal(this.appConfigStore.appAssets$, {
    initialValue: this.appConfigStore.value.assets,
  });
  private readonly styles = toSignal(this.appConfigStore.appStyles$, {
    initialValue: this.appConfigStore.value.styles,
  });

  protected readonly logoKey = computed(() => `${this.mode()}Logo`);
  protected readonly backgroundKey = computed(() => `${this.mode()}Background`);
  protected readonly primary = computed(() => this.findStyle('--primary-color'));
  protected readonly accent = computed(() => this.findStyle('--accent-color'));
  // Everything the upload zones show is derived from the store, so tab
  // switches (which re-create this component) and Cancel need no local state.
  protected readonly logo = computed(() => this.itemFor(this.logoKey(), 'Logo'));
  protected readonly background = computed(() => this.itemFor(this.backgroundKey(), 'Background'));

  onColorChange(id: string, color: string) {
    this.appConfigStore.updateStyle({ id, [this.mode()]: color });

    if (id !== '--accent-color') return;

    const accentColor = color.split(',').map((a) => parseInt(a.trim()));
    const accentLuminance = luminance(accentColor[0], accentColor[1], accentColor[2]);

    const fakeBody = document.createElement('body');
    fakeBody.style.display = 'none';
    document.body.parentNode?.appendChild(fakeBody);
    const lightColor = getComputedStyle(fakeBody)
      .getPropertyValue('--text-color')
      .replace(/\s*[\r\n]+\s*/gm, '')
      .trim();
    const lightLuminance = luminance(
      // @ts-ignore
      ...lightColor.split(',').map((a) => parseInt(a.trim()))
    );
    fakeBody.classList.add('dark');
    const darkColor = getComputedStyle(fakeBody)
      .getPropertyValue('--text-color')
      .replace(/\s*[\r\n]+\s*/gm, '')
      .trim();
    const darkLuminance = luminance(
      // @ts-ignore
      ...darkColor.split(',').map((a) => parseInt(a.trim()))
    );
    document.body.parentNode?.removeChild(fakeBody);

    if (contrast(lightLuminance, accentLuminance) > contrast(darkLuminance, accentLuminance)) {
      this.appConfigStore.updateStyle({
        id: '--contrast-color',
        [this.mode()]: lightColor,
      });
    } else {
      this.appConfigStore.updateStyle({
        id: '--contrast-color',
        [this.mode()]: darkColor,
      });
    }
  }

  /** Stages the (already size-fitted) file and previews it via its data URL. */
  async onFileSelected(file: File, key: string) {
    this.appConfigStore.setPendingAsset(key, file, await fileToDataUrl(file));
  }

  onFileRemoved(key: string) {
    this.appConfigStore.resetAsset(key);
  }

  private findStyle(id: string) {
    return this.styles().find((style) => style.id === id);
  }

  private itemFor(key: string, kind: 'Logo' | 'Background'): ImageUploadItem | undefined {
    const pending = this.appConfigStore.pendingFiles().get(key);
    const url = this.assets()[key]?.url;
    if (!url) {
      return undefined;
    }
    if (pending) {
      return { url, name: pending.name, size: pending.size, removable: true };
    }
    return {
      url,
      name: this.translateService.instant(kind === 'Logo' ? 'Current Logo' : 'Current Background'),
      removable: url !== configDefault.assets[key]?.url,
    };
  }
}
